import { observer } from "mobx-react-lite";
import type { NodeProps } from "@xyflow/react";
import { AddedEventButton } from "../../AddedEventButton";
import { useRootStore } from "../../../stores/hooks";
import { useFlowCanvasHandlers } from "./context";
import { HiddenHandles } from "./handles";
import type { FlowNodeData } from "../graph/types";
import type { BranchRef } from "../../../stores/automation/branches.store";
import * as A from "../../../stores/automation/mergers/actions";
import {
  actionsAtLocator,
  actionCardHighlight,
} from "../utils/action-highlight";
import { triggersIdsAvailableForMergeRecordsAction } from "../../../stores/automation/utils/inputValidation";
import {
  activateNextSection,
  handleActionChange,
  showActionError,
  showActionFieldMissingError,
  showActionFieldsEmptyError,
  showActionValidationErrors,
  showRecordListSourceError,
} from "../../../utils/action-helpers";

/**
 * React Flow wrapper over the legacy `AddedEventButton` action card. The node
 * carries only the action hash + its group locator; the live action data and
 * all validation/error state are read from the store here (observer), and the
 * delete/duplicate/change/click wiring is copied from the legacy render site
 * for the action's own group: root actions from `Sidebar/index.tsx`, loop
 * actions from `ActionGroup/Looping/index.tsx` (and `NestedLoopGroup.tsx`),
 * branch actions from `Branches/BranchColumn.tsx`.
 */
export const ActionNode = observer<NodeProps>(({ data }) => {
  const { actionHash, groupType, locator } = data as Extract<
    FlowNodeData,
    { kind: "action" }
  >;
  const store = useRootStore();
  const handlers = useFlowCanvasHandlers();
  const candidate = store.automations.candidate;
  const action = candidate.allActions.find((a) => a.hash === actionHash);
  if (action === undefined) return null;

  const branchesStore = candidate.branches;
  const looping = candidate.looping;
  const isRoot = locator.kind === "root";
  const branchRef: BranchRef | undefined =
    locator.kind === "branch"
      ? { instanceId: locator.instanceId, branchHash: locator.branchHash }
      : undefined;

  const isSubmitted = candidate.isActionSubmitted(action.hash);
  const isNewlyUnsubmitted =
    !isSubmitted && candidate.isNewlyAddedAction(action);

  // Root actions carry the extra merge-records-vs-trigger error + record-list
  // source error checks that Sidebar/index.tsx computes; loop/branch actions
  // use the shared field checks BranchColumn/Looping use.
  const error = isRoot
    ? showActionError(
        action,
        candidate.actionsSubmitted,
        triggersIdsAvailableForMergeRecordsAction,
        candidate.self.trigger.triggerReference?.triggerId,
      )
    : showActionError(
        action,
        candidate.actionsSubmitted,
        triggersIdsAvailableForMergeRecordsAction,
      );

  const isLoaded = store.automations.isAllDynamicDescriptionsLoaded();
  const fieldMissingError = isNewlyUnsubmitted
    ? false
    : isRoot
      ? showActionValidationErrors(isLoaded, candidate, action, isSubmitted) ||
        showRecordListSourceError(candidate, action, "actions")
      : showActionFieldsEmptyError(action, isSubmitted) ||
        showActionFieldMissingError(isLoaded, candidate.self, action);

  const handleDelete = (e: React.MouseEvent, currActionHash: string): void => {
    e.stopPropagation();
    if (branchRef !== undefined) {
      branchesStore.setBranchActions(
        branchRef,
        A.ejectActionReferences(currActionHash),
        A.ejectAction(currActionHash),
      );
      activateNextSection(
        currActionHash,
        branchesStore.branchActions(branchRef),
        handlers.onClickTrigger,
        handlers.onClickAction,
      );
      return;
    }
    if (locator.kind === "loop") {
      // Capture the loop's list BEFORE the eject (legacy Looping/index.tsx
      // computes loopingActions at render time): the deleted action must
      // still be findable so its previous neighbor gets selected.
      const preDeleteActions = looping.loopActionsAt(locator.instanceId);
      candidate.setActionsAt({ kind: "loop", instanceId: locator.instanceId }, [
        A.ejectActionReferences(currActionHash),
        A.ejectAction(currActionHash),
      ]);
      activateNextSection(
        currActionHash,
        preDeleteActions,
        handlers.onClickTrigger,
        handlers.onClickAction,
      );
      return;
    }
    // root: activate the next section against the pre-delete list, then eject,
    // matching Sidebar/index.tsx's ordering.
    activateNextSection(
      currActionHash,
      candidate.allActions,
      handlers.onClickTrigger,
      handlers.onClickAction,
    );
    candidate.setActions(
      [A.ejectActionReferences(action.hash), A.ejectAction(action.hash)],
      "actions",
    );
  };

  // Root actions leave onDuplicate unset -- AddedEventButton then falls back
  // to its internal duplicate merge (matching Sidebar/index.tsx). Loop/branch
  // actions write the duplicate back into their own group.
  const handleDuplicate =
    branchRef !== undefined
      ? (hash: string): void =>
          branchesStore.setBranchActions(
            branchRef,
            A.mergeDuplicateAction(hash),
          )
      : locator.kind === "loop"
        ? (hash: string): void =>
            candidate.setActionsAt(
              { kind: "loop", instanceId: locator.instanceId },
              [A.mergeDuplicateAction(hash)],
            )
        : undefined;

  const containerActions = actionsAtLocator(candidate, locator);
  const { isHighlighted, isActive } = actionCardHighlight(
    handlers.activeAction,
    actionHash,
    containerActions,
  );

  const preview = store.flowCanvas.loopSourcePreviewTarget;
  const isLoopSourcePreview =
    preview?.kind === "action" && preview.actionHash === actionHash;

  return (
    <>
      <HiddenHandles />
      <div data-loop-source-preview={String(isLoopSourcePreview)}>
        <AddedEventButton
          action={action}
          eventType="action"
          groupType={groupType}
          isActive={isActive}
          isHighlighted={isHighlighted}
          error={error}
          fieldMissingError={fieldMissingError}
          disabled={
            (action.accessError ?? false) ||
            (action.integrationDisabled ?? false) ||
            (action.actionDisabled ?? false)
          }
          onClick={() => {
            if (!action.accessError) handlers.onClickAction(actionHash);
          }}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onChange={(integrationId, actionId) => {
            handleActionChange(
              store,
              integrationId,
              actionId,
              groupType,
              handlers.onChangeAction,
              handlers.onClickAction,
            );
          }}
        />
      </div>
    </>
  );
});
ActionNode.displayName = "ActionNode";
